import { motion } from "motion/react";
import {
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  string,
} from "prop-types";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import styled from "styled-components/macro";
import { height } from "theme";

import classNames from "classnames";

import FnrLoadingBricks from "UI/FnrLoadingBricks2";
import { colors } from "UI/colors";

import { colorIsLight, lightenDarkenColor } from "Utilities/Colors/colorUtils";

// Content height for each size (line-height + vertical padding in FnrButtonContent)
// minus 12, which is what the bricks would measure on a button sitting still. Don't let
// them measure: a button mounting mid entrance animation gets tiny or giant bricks.
// Keep in sync with the FnrButtonContent size styles.
const BRICK_SIZES = {
  xsmall: 13,
  small: 18,
  default: 28,
  large: 36,
  xlarge: 46,
  xxlarge: 44,
};

/**
 * @typedef {Object} FnrButtonProps
 * @property {React.ReactNode} [children] - Content to render inside the button
 * @property {string} [text] - Alternative way to provide button text (use children instead if passing JSX)
 * @property {boolean} [loading=false] - Shows loading animation when true, disables interaction
 * @property {Function} [onClick] - Click handler function
 * @property {string} [href] - When provided, button renders as a react-router Link to this path
 * @property {Function} [onHoverStart] - Callback fired when hover starts
 * @property {Function} [onHoverEnd] - Callback fired when hover ends
 * @property {boolean} [forceHover=false] - Force the button to appear in hover state
 * @property {boolean} [disabled=false] - Disables the button and applies disabled styling
 * @property {string} [color="gray"] - Button color. Accepts hex values (e.g., "#ff0000") or named colors (e.g., "gray", "blue", "red", "green", "purple", "yellow", "orange", "teal", "pink")
 * @property {("xsmall"|"small"|"large"|"xlarge"|"xxlarge")} [size] - Button size: "xsmall", "small", "large", "xlarge", or "xxlarge". Omit for default/medium size
 * @property {boolean} [fullWidth=false] - Makes the button take full width of its container
 * @property {number|string} [borderRadius=9999] - Border radius in pixels (number) or as CSS value (string)
 * @property {string} [innerBorderRadius] - Inner border radius, defaults to borderRadius - 2px
 * @property {Object} [style] - Additional inline styles to apply
 * @property {Function} [onTouchEnd] - Touch end event handler
 * @property {string} [focusSize] - Size of focus outline (e.g., "2px")
 * @property {string} [className] - Additional CSS class names
 * @property {boolean} [stopPropagation=false] - If true, stops event propagation on click
 * @property {boolean} [preventDefault=false] - If true, prevents default behavior on click
 * @property {number} [height] - Explicit height value in pixels
 * @property {number|string} [paddingLeft] - Overrides horizontal padding on the left side
 * @property {number|string} [paddingRight] - Overrides horizontal padding on the right side
 * @property {string} [disabledColor] - Background color when disabled (hex value, e.g. "#d1d5db"). Replaces the default grayscale filter with a flat color.
 * @property {number} [loadingBrickSize] - Brick size in px for the loading state. Overrides the size-based default; use it when restyling `.FnrButtonContent` changes the button's height.
 */

/**
 * A beautiful, interactive button component with hover effects, shine animations, and loading states.
 *
 * @example
 * // Basic usage
 * <FnrButton color="blue" onClick={() => console.log('clicked')}>
 *   Click Me
 * </FnrButton>
 *
 * @example
 * // With loading state
 * <FnrButton color="green" loading={isLoading} onClick={handleSubmit}>
 *   Submit
 * </FnrButton>
 *
 * @example
 * // As a link
 * <FnrButton color="red" href="/dashboard">
 *   Go to Dashboard
 * </FnrButton>
 *
 * @example
 * // With custom size and full width
 * <FnrButton color="purple" size="large" fullWidth>
 *   Large Full Width Button
 * </FnrButton>
 *
 * @type {React.ForwardRefExoticComponent<FnrButtonProps & React.RefAttributes<HTMLElement>>}
 */
const FnrButton = forwardRef(
  (
    {
      children,
      text,
      loading = false,
      onClick,
      href,
      onHoverStart,
      onHoverEnd,
      forceHover = false,
      disabled = false,
      color = "gray",
      size,
      fullWidth,
      borderRadius = 9999,
      innerBorderRadius,
      style = {},
      onTouchEnd,
      focusSize,
      className,
      stopPropagation,
      preventDefault,
      height: heightArg,
      paddingLeft,
      paddingRight,
      disabledColor,
      loadingBrickSize,
      ...props
    },
    forwardedRef
  ) => {
    const internalRef = useRef();
    const measurements = useRef();

    const buttonRef = forwardedRef || internalRef;

    const measureButton = useCallback(() => {
      if (!buttonRef.current) return;

      measurements.current = buttonRef.current.getBoundingClientRect();
      setMeasured(true);
    }, [buttonRef]);

    useEffect(() => {
      measureButton();
    }, [measureButton]);

    const [measured, setMeasured] = useState(false);
    const [tapping, setTapping] = useState(false);
    const [hovering, setHovering] = useState(false);

    const hoverStart = (e) => {
      onHoverStart && onHoverStart(e);
      if (!measurements.current) measureButton();
      setHovering(true);
    };
    const hoverEnd = (e) => {
      onHoverEnd && onHoverEnd(e);
      setHovering(false);
    };

    let hexColor = "#98a4a5";
    if (color.substring(0, 1) === "#") hexColor = color;
    else {
      const colorName = color.charAt(0).toUpperCase() + color.slice(1);
      if (colors["fn" + colorName]) {
        hexColor = colors["fn" + colorName];
      }
    }

    const hilightColor = lightenDarkenColor(hexColor, 18);
    const shineColor = lightenDarkenColor(hexColor, 40);

    const darkText = colorIsLight(lightenDarkenColor(hexColor, -40));

    const updatePointer = useCallback(
      ({ offsetX, offsetY }) => {
        if (!buttonRef.current) return;

        buttonRef.current.style.setProperty(
          "--fnrButtonShineX",
          (offsetX / measurements.current.width) * 100 + "%"
        );
        buttonRef.current.style.setProperty(
          "--fnrButtonShineY",
          (offsetY / measurements.current.height) * 100 + "%"
        );
      },
      [buttonRef]
    );

    useEffect(() => {
      const button = buttonRef.current;
      if (!button) return;
      measureButton();
      button.addEventListener("pointermove", updatePointer);
      button.addEventListener("mouseover", measureButton);
      return () => {
        button.removeEventListener("pointermove", updatePointer);
        button.removeEventListener("mouseover", measureButton);
      };
    }, [buttonRef, measureButton, updatePointer]);

    const tapStart = () => setTapping(true);
    const tapEnd = () => setTapping(false);

    const useDisabledColor = disabled && disabledColor;
    const bgColor = useDisabledColor ? disabledColor : hexColor;

    const styles = {
      backgroundColor: bgColor,
      "--fnrButtonShineX": "50%",
      "--fnrButtonShineY": "50%",
      "--fnrButtonBgColor": bgColor,
      "--fnrButtonHilightColor": useDisabledColor
        ? disabledColor
        : hilightColor,
      "--fnrButtonShineColor": useDisabledColor ? disabledColor : shineColor,
    };

    if (measured) {
      styles["--fnrButtonShineRadius"] =
        Math.max(80, measurements.current.width, measurements.current.height) *
          2 +
        "px";
    }

    style = { ...styles, ...style };

    const handleClick = (e) => {
      if (stopPropagation) e.stopPropagation();
      if (preventDefault) e.preventDefault();
      if (onClick && !disabled) onClick();
    };

    if (typeof borderRadius === "number") {
      if (!innerBorderRadius) innerBorderRadius = borderRadius - 2 + "px";
      borderRadius = borderRadius + "px";
    }

    return (
      <FnrButtonWrapper
        disabled={href ? undefined : disabled}
        data-cy="fnrButton"
        ref={buttonRef}
        onHoverStart={href ? undefined : hoverStart}
        onHoverEnd={href ? undefined : hoverEnd}
        onTapStart={href ? undefined : tapStart}
        onTapCancel={href ? undefined : tapEnd}
        onTap={href ? undefined : tapEnd}
        onClick={handleClick}
        as={href ? Link : null}
        to={href}
        target={href ? "_blank" : null}
        borderradius={borderRadius}
        $focusSize={focusSize}
        $height={heightArg}
        $noHeight={!heightArg} // Don't let the interpolation set height to 100%.
        $disabledColor={useDisabledColor ? disabledColor : undefined}
        className={`${classNames(size, {
          loading,
          disabled,
          forceHover,
          fullWidth,
          darkText,
        })} ${className}`}
        style={style}
        {...props}
      >
        <Border borderRadius={borderRadius}>
          <BorderHilight />
        </Border>
        <Inside borderRadius={innerBorderRadius || borderRadius}>
          <Shine />
          <Shadow />
          <ContentAround>
            <FnrLoadingBricks
              loading={loading}
              size={
                loadingBrickSize ?? BRICK_SIZES[size] ?? BRICK_SIZES.default
              }
            >
              <FnrButtonContent
                className="FnrButtonContent"
                style={{
                  ...(paddingLeft != null && { paddingLeft }),
                  ...(paddingRight != null && { paddingRight }),
                }}
                animate={
                  disabled || loading
                    ? null
                    : {
                        scale: tapping
                          ? size === "small" || size === "xsmall"
                            ? 0.97
                            : 0.96
                          : hovering
                          ? size === "small" || size === "xsmall"
                            ? 1.03
                            : 1.04
                          : 1,
                      }
                }
              >
                {text}
                {children}
              </FnrButtonContent>
            </FnrLoadingBricks>
          </ContentAround>
        </Inside>
      </FnrButtonWrapper>
    );
  }
);

FnrButton.displayName = "FnrButton";
export default FnrButton;

export const FnrButtonWrapper = styled(motion.button)`
  width: fit-content;
  display: block;
  background: var(--fnrButtonBgColor);
  padding: 2px;
  border-radius: ${(props) => props.borderradius};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  appearance: none;
  border: none;
  box-shadow: rgba(0, 0, 0, 0.2) 1px 1px 2px;
  text-decoration: none !important;
  transition: filter 0.2s, background-color 0.2s;
  &.fullWidth {
    display: block;
    width: 100%;
  }
  &.loading {
    cursor: wait;
  }
  &.disabled {
    filter: ${({ $disabledColor }) =>
      $disabledColor ? "none" : "grayscale(25%)"};
    cursor: ${({ $disabledColor }) =>
      $disabledColor ? "default" : "not-allowed"};
    pointer-events: ${({ $disabledColor }) =>
      $disabledColor ? "none" : "auto"};
    box-shadow: ${({ $disabledColor }) =>
      $disabledColor ? "none" : "rgba(0, 0, 0, 0.2) 1px 1px 2px"};
  }
  &.xsmall,
  &.small {
    padding: 2px;
  }
  &:focus {
    outline: ${({ $focusSize }) =>
      $focusSize
        ? `${$focusSize || "1px"} solid var(--fnrButtonShineColor)`
        : `none`};
    outline-offset: 1px;
    text-decoration: none !important;
  }
  &:hover {
    text-decoration: none !important;
  }
  ${height}
`;
const Inside = styled.div`
  background: var(--fnrButtonBgColor);
  border-radius: ${(props) => props.borderRadius};
  display: flex;
  height: 100%;
  align-items: center;
  position: relative;
  z-index: 5;
  overflow: hidden;
  transition: background 0.2s;

  &::before {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: radial-gradient(
      calc(var(--fnrButtonShineRadius) * 0.8) circle at 28% 20%,
      var(--fnrButtonHilightColor),
      transparent 40%
    );
    /* dumb old safari */
    @supports not (aspect-ratio: auto) {
      background: radial-gradient(
        calc(var(--fnrButtonShineRadius) * 4) circle at 28% 20%,
        var(--fnrButtonHilightColor),
        transparent 40%
      );
    }
    z-index: -1;
  }
`;
export const FnrButtonContent = styled(motion.div)`
  color: #fff;
  font-weight: 400;
  position: relative;
  z-index: 20;
  user-select: none;
  font-size: 18px;
  line-height: 22px;
  padding: 9px 15px;
  text-shadow: rgba(0, 0, 0, 0.2) 1px 1px 2px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    position: absolute;
    content: "";
    z-index: 100;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0);
  }

  // prettier-ignore
  ${FnrButtonWrapper}.darkText & {
    color: #13426b;
  }

  // prettier-ignore
  ${FnrButtonWrapper}.fullWidth & {
    justify-content: center;
  }

  ${FnrButtonWrapper}.xxlarge & {
    font-size: 28px;
    line-height: 32px;
    padding: 12px 24px;
  }

  ${FnrButtonWrapper}.xlarge & {
    font-size: 26px;
    line-height: 30px;
    padding: 14px 23px;
  }

  ${FnrButtonWrapper}.large & {
    font-size: 22px;
    line-height: 24px;
    padding: 12px 19px;
  }

  ${FnrButtonWrapper}.small & {
    font-size: 14px;
    line-height: 18px;
    padding: 6px 11px;
  }

  ${FnrButtonWrapper}.xsmall & {
    font-size: 13px;
    line-height: 17px;
    padding: 4px 7px;
  }
`;
const ContentAround = styled.div`
  z-index: 40;
  position: relative;
  width: 100%;
`;

const Border = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: ${(props) => props.borderRadius};
  z-index: 1;
  overflow: hidden;
`;
const BorderHilight = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: radial-gradient(
    var(--fnrButtonShineRadius) circle at var(--fnrButtonShineX)
      var(--fnrButtonShineY),
    rgba(255, 255, 255, 0.6),
    transparent 40%
  );
  opacity: 0;
  transition: opacity 1s, background 0.2s;

  ${FnrButtonWrapper}:not(.loading):not(.disabled):hover &,
  ${FnrButtonWrapper}:not(.loading):not(.disabled):focus-visible &,
  ${FnrButtonWrapper}.forceHover & {
    opacity: 1;
    transition: opacity 0.5s, background 0.2s;
  }
`;

export const Shine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: radial-gradient(
    var(--fnrButtonShineRadius) circle at var(--fnrButtonShineX)
      var(--fnrButtonShineY),
    var(--fnrButtonShineColor),
    transparent 40%
  );
  /* dumb old safari */
  @supports not (aspect-ratio: auto) {
    background: radial-gradient(
      calc(var(--fnrButtonShineRadius) * 5) circle at var(--fnrButtonShineX)
        var(--fnrButtonShineY),
      var(--fnrButtonShineColor),
      transparent 40%
    );
  }
  opacity: 0;
  transition: opacity 0.8s, background 0.2s;
  z-index: 3;

  ${FnrButtonWrapper}:not(.loading):not(.disabled):hover &,
  ${FnrButtonWrapper}:not(.loading):not(.disabled):focus &,
  ${FnrButtonWrapper}.forceHover & {
    opacity: 1;
    transition: opacity 0.4s, background 0.2s;
  }
`;
const Shadow = styled.div`
  position: absolute;
  z-index: 21;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0);
  border-radius: inherit;
  transition: box-shadow 0.2s;
  box-shadow: inset rgba(0, 0, 0, 0.1) 0 0 3px;
  ${FnrButtonWrapper}:not(.loading):active & {
    box-shadow: inset rgba(0, 0, 0, 0.2) 0 0 5px;
  }
`;

FnrButton.propTypes = {
  children: node,
  text: string,
  loading: bool,
  onClick: func,
  href: string,
  onHoverStart: func,
  onHoverEnd: func,
  forceHover: bool,
  disabled: bool,
  color: string,
  size: string,
  fullWidth: bool,
  borderRadius: oneOfType([number, string]),
  innerBorderRadius: string,
  style: object,
  onTouchEnd: func,
  focusSize: string,
  className: string,
  stopPropagation: bool,
  preventDefault: bool,
  height: number,
  paddingLeft: oneOfType([number, string]),
  paddingRight: oneOfType([number, string]),
  disabledColor: string,
  loadingBrickSize: number,
};
